/* eslint-disable indent */
import { EPostStatus } from '@constants';
import { dateProvider } from '@utils';
import { Button, Divider, Drawer, Flex, Image, Modal, Popover, Tooltip } from 'antd';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import {
  DateWrapper,
  FeaturedTag,
  ImageWrapper,
  PostContentWrapper,
  SubtitleWrapper,
  TitleWrapper
} from './PostViewer.style';

import { Icon } from '@/components/Icon';
import { RelatedPostsToPost } from '@/components/RelatedPosts';
import { useIsMobile, usePostViewer } from '@/hooks';
import { useMutatePost, usePostDetail, useProfile } from '@/store';

const ICON_SIZE = 20;

export const PostViewer = () => {
  const { t } = useTranslation('CommonDashboardTranslations');
  const { t: tDefault } = useTranslation('DefaultTranslations');
  const { postId, setPostId } = usePostViewer();
  const { post } = usePostDetail(postId);
  const { roleCheck } = useProfile();
  const { deletePost, isPending } = useMutatePost();
  const isMobile = useIsMobile();

  const DRAWER_WIDTH = isMobile ? '100%' : 600;

  if (!post) return null;

  const {
    _id,
    title,
    topic,
    content,
    tags,
    status,
    isFeatured,
    createdAt,
    featuredImage,
    scheduledPublishDate
  } = post;

  const { isManager, isAdmin, isTeacher } = roleCheck;
  const canEdit = isManager || isAdmin || isTeacher;

  const tagCount = tags?.length || 0;
  const tagsNames = tags?.map(tag => tag.name).join(', ');

  const isScheduled = canEdit && status === EPostStatus.SCHEDULED && scheduledPublishDate;

  const handleClose = () => {
    setPostId(undefined);
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('DELETE_POST_TITLE'),
      content: t('DELETE_POST_CONTENT'),
      okText: t('DELETE'),
      cancelText: tDefault('CANCEL'),
      okButtonProps: { danger: true, loading: isPending },
      onOk: () => {
        deletePost(_id, { onSuccess: handleClose });
      }
    });
  };

  const showDeleteButton = canEdit && status === EPostStatus.PUBLISHED;

  const drawerTitle = showDeleteButton ? (
    <Flex justify="flex-end">
      <Tooltip title={t('DELETE')}>
        <Button type="text" icon={<Icon i="TrashCan" size={ICON_SIZE} />} onClick={handleDelete} />
      </Tooltip>
    </Flex>
  ) : null;

  return (
    <Drawer open={!!postId} width={DRAWER_WIDTH} onClose={handleClose} title={drawerTitle}>
      {isFeatured && <FeaturedTag>{t('FEATURED_NEWS')}</FeaturedTag>}
      <Flex vertical gap={24}>
        <Flex vertical gap={14}>
          <TitleWrapper>{title}</TitleWrapper>
          <SubtitleWrapper>
            <DateWrapper isscheduled={isScheduled ? 'true' : 'false'}>
              <div>
                {scheduledPublishDate
                  ? dateProvider.format(
                      dateProvider.zonedTimeToUtc(scheduledPublishDate),
                      'dd MMM, yyyy'
                    )
                  : dateProvider.format(createdAt, 'dd MMM, yyyy')}
              </div>
              {isScheduled && (
                <Tooltip
                  title={dateProvider.format(
                    dateProvider.zonedTimeToUtc(scheduledPublishDate),
                    'dd/MM/yyyy HH:mm'
                  )}>
                  <Icon i="Time" />
                </Tooltip>
              )}
            </DateWrapper>
            <span> • </span>
            {topic?.name}
            {tagCount > 0 && (
              <>
                <span> • </span>
                <Popover title={t('TAGS')} content={<div>{tagsNames}</div>}>
                  <div>{t('POST_TAGS_COUNT', { count: tagCount })}</div>
                </Popover>
              </>
            )}
          </SubtitleWrapper>
        </Flex>
        <ImageWrapper>
          <Image width="100%" height={320} preview={false} alt={title} src={featuredImage?.url} />
        </ImageWrapper>
        <PostContentWrapper
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content ?? '') }}
        />
      </Flex>
      <Divider />
      <RelatedPostsToPost postId={_id} />
    </Drawer>
  );
};
